/**
 * Talking to the Complaudax browser extension from the dashboard.
 *
 * The extension starts the pairing and puts its own runtime id in the URL, so
 * the user never has to read one off chrome://extensions. That id decides which
 * extension gets to pair with this account, so it is checked against the ids
 * this build knows about: without that check, a crafted
 * /extension/authorize?ext=... link would pair whatever extension an attacker
 * names.
 *
 * What crosses to the extension is a single-use pairing code, never this page's
 * Supabase tokens. The extension redeems the code at the API, which mints a
 * session directly to it.
 */

// Shared with the landing site's "Add to Chrome" buttons; see packages/links.
import { CHROME_EXTENSION_ID } from "@shared/links";

export { CHROME_STORE_URL } from "@shared/links";

/** Comma-separated, so an unpacked build and the published one can both be
 *  allowed while a release is rolling out.
 *
 *  Defaults to the published id rather than to empty. manifest.json pins `key`,
 *  so that id is fixed across releases and the API already states it the same
 *  way (DEFAULT_EXTENSION_IDS in backend/server.py). Leaving the default empty
 *  meant a Pages build that forgot the variable shipped a dashboard that
 *  refused every pairing attempt, which looks like a broken extension rather
 *  than missing configuration. The variable still overrides, which is what a
 *  rollout onto a new id needs. */
export const EXTENSION_IDS = (process.env.REACT_APP_EXTENSION_ID || CHROME_EXTENSION_ID)
  .split(",")
  .map((id) => id.trim())
  .filter(Boolean);

const BEGIN_PAIRING_MESSAGE = "COMPLAUDAX_BEGIN_PAIRING";
const PAIRING_CODE_MESSAGE = "COMPLAUDAX_PAIRING_CODE";

/** Chrome derives extension ids as 32 characters from the first half of the alphabet. */
const EXTENSION_ID_PATTERN = /^[a-p]{32}$/;

/** An unpacked build gets a fresh id per checkout path, so a developer running
 *  the dashboard locally pairs with whatever they have loaded. A deployed build
 *  accepts a configured id only. */
export const isLocalDashboard = () =>
  /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);

export class ExtensionError extends Error {
  constructor(code, message) {
    super(message);
    this.name = "ExtensionError";
    this.code = code;
  }
}

/**
 * Resolves which extension this account may be paired with.
 *
 * `requested` is the `ext` query parameter the extension appended. It is absent
 * when the user reaches this page from the sidebar instead, which falls back to
 * the first configured id.
 *
 * Returns `{ id }` on success or `{ error }` with a message meant for the user.
 */
export function resolveExtensionId(requested) {
  const id = (requested || "").trim() || EXTENSION_IDS[0] || "";

  if (!id) {
    return {
      error: isLocalDashboard()
        ? "No extension id. Start from the side panel's Connect button, or set REACT_APP_EXTENSION_ID."
        : "This dashboard is not configured with an extension id. Contact support.",
    };
  }
  if (!EXTENSION_ID_PATTERN.test(id)) {
    return { error: "That is not a valid Chrome extension id." };
  }
  // Fail closed: an unconfigured deployment must not pair with an arbitrary id
  // just because the URL asked it to.
  if (!isLocalDashboard() && !EXTENSION_IDS.includes(id)) {
    return { error: "That extension is not the published Complaudax extension." };
  }
  return { id };
}

/**
 * `externally_connectable` in the extension's manifest is what allows these
 * calls at all, so a build that does not list this origin never answers and the
 * promise rejects as unreachable.
 */
function messageExtension(extensionId, message) {
  return new Promise((resolve, reject) => {
    const runtime = window.chrome?.runtime;
    if (!runtime?.sendMessage) {
      reject(new ExtensionError("unsupported-browser", "This browser cannot connect extensions."));
      return;
    }
    runtime.sendMessage(extensionId, message, (response) => {
      // Reading lastError is what marks it handled; leaving it unread logs a
      // console error on every failed attempt.
      if (runtime.lastError) {
        reject(new ExtensionError("unreachable", "The extension did not respond."));
        return;
      }
      if (!response?.ok) {
        reject(new ExtensionError("refused", response?.error || "The extension refused the request."));
        return;
      }
      resolve(response);
    });
  });
}

/** Asks the extension to open a pairing window and return the state that binds it. */
export async function beginPairing(extensionId) {
  const { state } = await messageExtension(extensionId, { type: BEGIN_PAIRING_MESSAGE });
  if (!state) {
    throw new ExtensionError("refused", "The extension did not open a pairing window.");
  }
  return state;
}

/** Delivers the approved code. The extension redeems it; this page never sees a session. */
export function deliverPairingCode(extensionId, code, state) {
  return messageExtension(extensionId, { type: PAIRING_CODE_MESSAGE, code, state });
}
